import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, DollarSign, Send, CheckCircle, ArrowRight } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

const EVENT_TYPES = ["Premium Wedding", "Corporate Event", "Yacht & Sea Event", "VIP Private Party", "Product Launch", "Nightlife Event", "Charity Gala", "Other"];
const BUDGET_RANGES = ["$3,000 - $5,000", "$5,000 - $10,000", "$10,000 - $25,000", "$25,000+", "Custom / Discuss"];
const SERVICE_OPTIONS = ["Signature Photography", "Cinematic Videography", "Drone Coverage", "High-Tech Capture", "VIP Package", "Custom Experience"];

const SERVICE_MAP = {
  "Signature Event Photography": "Signature Photography",
  "Cinematic Videography": "Cinematic Videography",
  "Drone Coverage": "Drone Coverage",
  "High-Tech Event Capture": "High-Tech Capture",
  "VIP Event Packages": "VIP Package",
  "Custom Premium Experiences": "Custom Experience",
};

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "", event_date: "", location: "", budget: "", services: [], message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const serviceParam = searchParams.get("service");
    if (serviceParam) {
      const mapped = SERVICE_MAP[serviceParam] || serviceParam;
      if (SERVICE_OPTIONS.includes(mapped)) {
        setForm((prev) => ({ ...prev, services: [mapped] }));
      }
    }
  }, [searchParams]);

  const toggleService = (svc) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(svc) ? prev.services.filter((s) => s !== svc) : [...prev.services, svc],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type || !form.event_date) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, form);
      setBookingId(res.data.id);
      setSubmitted(true);
      toast.success("Your inquiry has been received.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="booking-success" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-lg px-6">
          <CheckCircle size={48} className="text-gold mx-auto mb-6" />
          <h2 className="font-heading text-3xl text-white mb-4">Inquiry Received</h2>
          <p className="text-white/60 font-body font-light text-sm leading-relaxed mb-3">
            Thank you for your interest in Aristocrats JA. Our concierge team will review your inquiry and respond within 24 hours.
          </p>
          <p className="text-white/30 font-body text-xs mb-8">
            Booking Reference: <span className="text-gold font-medium">{bookingId}</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`/payment?booking=${bookingId}`}
              data-testid="booking-proceed-to-payment"
              className="inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors"
            >
              Proceed to Payment <ArrowRight size={16} />
            </Link>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-body font-light text-sm px-8 py-3 hover:border-white/40 transition-colors">
              Return Home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="pt-20">
      <section className="py-24 md:py-32 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Book Now</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              Begin Your<br /><span className="gold-gradient-text">Exclusive Experience</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base leading-relaxed">
              Complete the form below and our concierge team will craft a personalized proposal for your event.
            </motion.p>
          </motion.div>

          <motion.form
            initial="hidden"
            animate="visible"
            onSubmit={handleSubmit}
            className="max-w-3xl"
            data-testid="booking-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <motion.div variants={fadeUp} custom={3}>
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Full Name *</label>
                <Input
                  data-testid="booking-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm focus:border-gold/50"
                />
              </motion.div>
              <motion.div variants={fadeUp} custom={4}>
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Email *</label>
                <Input
                  data-testid="booking-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="your@email.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm focus:border-gold/50"
                />
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <motion.div variants={fadeUp} custom={5}>
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Phone</label>
                <Input
                  data-testid="booking-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 (876) 000-0000"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm focus:border-gold/50"
                />
              </motion.div>
              <motion.div variants={fadeUp} custom={6}>
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Event Type *</label>
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger data-testid="booking-event-type" className="bg-white/5 border-white/10 text-white h-12 rounded-sm">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="bg-luxury-charcoal border-white/10">
                    {EVENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="text-white/80 focus:bg-gold/10 focus:text-white">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <motion.div variants={fadeUp} custom={7}>
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Calendar size={14} className="text-gold" /> Event Date *
                </label>
                <Input
                  data-testid="booking-date"
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="bg-white/5 border-white/10 text-white h-12 rounded-sm focus:border-gold/50"
                />
              </motion.div>
              <motion.div variants={fadeUp} custom={8}>
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 flex items-center gap-2">
                  <MapPin size={14} className="text-gold" /> Location
                </label>
                <Input
                  data-testid="booking-location"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Venue or city"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm focus:border-gold/50"
                />
              </motion.div>
            </div>

            <motion.div variants={fadeUp} custom={9} className="mb-6">
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 flex items-center gap-2">
                <DollarSign size={14} className="text-gold" /> Budget Range
              </label>
              <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                <SelectTrigger data-testid="booking-budget" className="bg-white/5 border-white/10 text-white h-12 rounded-sm">
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent className="bg-luxury-charcoal border-white/10">
                  {BUDGET_RANGES.map((b) => (
                    <SelectItem key={b} value={b} className="text-white/80 focus:bg-gold/10 focus:text-white">{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </motion.div>

            <motion.div variants={fadeUp} custom={10} className="mb-6">
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-3 block">Services of Interest</label>
              <div className="flex flex-wrap gap-3">
                {SERVICE_OPTIONS.map((svc) => (
                  <button
                    key={svc}
                    type="button"
                    data-testid={`service-option-${svc.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => toggleService(svc)}
                    className={`px-4 py-2 text-sm font-body font-light transition-all ${
                      form.services.includes(svc)
                        ? "bg-gold/20 border border-gold/50 text-gold"
                        : "border border-white/10 text-white/60 hover:border-white/30"
                    }`}
                  >
                    {svc}
                  </button>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} custom={11} className="mb-8">
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Additional Details</label>
              <Textarea
                data-testid="booking-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us about your vision..."
                rows={4}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm focus:border-gold/50 resize-none"
              />
            </motion.div>

            <motion.div variants={fadeUp} custom={12}>
              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-10 py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
              >
                {loading ? "Submitting..." : "Submit Inquiry"} <Send size={16} />
              </button>
            </motion.div>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
